import axiosInstance from '@/setup/axios/axiosInstance';
import { PermissionGroup } from '../models/permission';
import { Role, UserRole } from '../models/Role';
import { Company } from '@/modules/organizations/models/CompanyModel';

export const getPermissions = async (companyId?: string): Promise<PermissionGroup[]> => {
  try {
    const normalizedCompanyId = String(companyId || '').trim();
    const requestConfig = normalizedCompanyId
      ? { headers: { 'X-Selected-Company': normalizedCompanyId } }
      : undefined;

    const response = await axiosInstance.get('/accounts/roles/permissions/', requestConfig);
    return response.data;
  } catch (error) {
    console.error('Error fetching permissions:', error);
    throw error;
  }
};

export const getRolePermissions = async (roleId: string): Promise<PermissionGroup[]> => {
  try {
    const response = await axiosInstance.get(`/accounts/roles/${roleId}/permissions/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching role permissions:', error);
    throw error;
  }
};

export const getRoleCurrentPermissions = async (roleId: string): Promise<{[key: string]: string[]}> => {
  try {
    const response = await axiosInstance.get(`/accounts/roles/${roleId}/current_permissions/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching role current permissions:', error);
    throw error;
  }
};

export const getRolesByCompany = async (companyId: string, level?: string): Promise<Role[]> => {
  try {
    const params = new URLSearchParams();
    params.append('no_pagination', 'true');
    if (companyId) {
      params.append('company', companyId);
    }
    if (level) {
      params.append('level', level);
    }
    
    const url = `/accounts/roles/${params.toString() ? '?' + params.toString() : ''}`;
    const response = await axiosInstance.get(url);
    return response.data.results || response.data;
  } catch (error) {
    console.error('Error fetching roles by company:', error);
    throw error;
  }
};

export const getRolesByLevel = async (level: string): Promise<Role[]> => {
  try {
    const response = await axiosInstance.get(`/accounts/roles/by_level/?level=${level}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching roles by level:', error);
    throw error;
  }
};

export const getRolesByDepartment = async (departmentId: string): Promise<Role[]> => {
  try {
    const response = await axiosInstance.get(`/accounts/roles/by_department/?department_id=${departmentId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching roles by department:', error);
    throw error;
  }
};

export const getRoleById = async (roleId: string): Promise<Role> => {
  try {
    const response = await axiosInstance.get(`/accounts/roles/${roleId}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching role by ID:', error);
    throw error;
  }
};

export const updateRole = async (roleId: string, roleData: any): Promise<Role> => {
  try {
    const response = await axiosInstance.put(`/accounts/roles/${roleId}/`, roleData);
    return response.data;
  } catch (error) {
    console.error('Error updating role:', error);
    throw error;
  }
};

export const createRole = async (roleData: any): Promise<Role> => {
  try {
    const response = await axiosInstance.post('/accounts/roles/', roleData);
    return response.data;
  } catch (error) {
    console.error('Error creating role:', error);
    throw error;
  }
};



export const getCompanies = async (): Promise<Company[]> => {
  try {
    const response = await axiosInstance.get('/companies/');
    const payload = response.data;
    if (Array.isArray(payload)) {
      return payload;
    }
    return Array.isArray(payload?.results) ? payload.results : [];
  } catch (error) {
    console.error('Error fetching companies:', error);
    throw error;
  }
};

// UserRole API functions
export const getUserRoles = async (): Promise<UserRole[]> => {
  try {
    const response = await axiosInstance.get('/accounts/user-roles/');
    return response.data;
  } catch (error) {
    console.error('Error fetching user roles:', error);
    throw error;
  }
};

export const getUserRolesByUser = async (userId: string): Promise<UserRole[]> => {
  try {
    const response = await axiosInstance.get(`/accounts/user-roles/by_user/?user_id=${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user roles by user:', error);
    throw error;
  }
};

export const getUserRolesByCompany = async (companyId: string): Promise<UserRole[]> => {
  try {
    const response = await axiosInstance.get(`/accounts/user-roles/by_company/?company_id=${companyId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user roles by company:', error);
    throw error;
  }
};

export const createUserRole = async (userRoleData: {
  user: string;
  role: string;
  company: string;
  is_primary?: boolean;
}): Promise<UserRole> => {
  try {
    const response = await axiosInstance.post('/accounts/user-roles/', userRoleData);
    return response.data;
  } catch (error) {
    console.error('Error creating user role:', error);
    throw error;
  }
};

export const updateUserRole = async (userRoleId: string, userRoleData: any): Promise<UserRole> => {
  try {
    const response = await axiosInstance.put(`/accounts/user-roles/${userRoleId}/`, userRoleData);
    return response.data;
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

export const deleteUserRole = async (userRoleId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/accounts/user-roles/${userRoleId}/`);
  } catch (error) {
    console.error('Error deleting user role:', error);
    throw error;
  }
};

// Delete multiple roles
export const deleteRoles = async (roleIds: string[]): Promise<void> => {
  try {
    // Delete roles one by one (backend doesn't support bulk delete)
    const deletePromises = roleIds.map(roleId => 
      axiosInstance.delete(`/accounts/roles/${roleId}/`)
    );
    await Promise.all(deletePromises);
  } catch (error) {
    console.error('Error deleting roles:', error);
    throw error;
  }
};

// Check if role name is unique
export const checkRoleNameUnique = async (name: string, level: string, companyId?: string, departmentId?: string, excludeId?: string): Promise<boolean> => {
  try {
    const params = new URLSearchParams();
    params.append('name', name);
    params.append('level', level);
    if (companyId) params.append('company', companyId);
    if (departmentId) params.append('department', departmentId);
    if (excludeId) params.append('exclude_id', excludeId);
    
    const response = await axiosInstance.get(`/accounts/roles/check_unique/?${params.toString()}`);
    return response.data.is_unique;
  } catch (error) {
    console.error('Error checking role name uniqueness:', error);
    return true; // Assume unique if check fails
  }
};
